import { Component, Input, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { HttpClientModule } from '@angular/common/http';
import { MaterialModule } from '../../shared/material.module';
import { environment } from 'src/environments/environment';
import { BreadcrumbComponent } from 'src/app/components/shared/breadcrumb/breadcrumb.component';
import {
  CardsService,
  PersonalInfo,
  BusinessInfo,
  Address,
  Contact,
  social_links,
  quick_links,
  ThemeColors,
  CreateCardForm,
  CardCreateRequest,
  BusinessError,
  Leader,
  Permissions,
  Entity,
  Branches,
} from '../../services/cards.service';
import { AuthService } from '../../services/auth.service';
import { Router } from '@angular/router';
import {
  LANGUAGE_OPTIONS,
  CARD_TYPE_OPTIONS,
  INDUSTRY_OPTIONS,
} from '../../constants/app.constants';
import { PlatformConfigService, Category } from '../../services/platform-config.service';

export interface DigitalCard {
  id: string;
  handler: string;
  personalInfo?: PersonalInfo;
  business_info?: BusinessInfo;
  contact?: Contact;
  address?: Address;
  social_links?: (social_links & { label?: string; openInNewTab?: boolean })[]; // New structure (array of SocialLink objects)
  quick_links?: (quick_links & {
    description?: string;
    openInNewTab?: boolean;
  })[];
  themeColors?: ThemeColors;
  isVerified: boolean;
  createdAt: string;
  updatedAt: string;
  language?: string;
  type?: string;
  industry?: string;
  email?: string;
  phone?: string;
  primary_color_1?: string;
  primary_color_2?: string;
  seoImage?: string; // URL to SEO image
  leaders?: Leader[];
  employeescount?: number;
  permissions?: {
    product?: PermissionValue;
    service?: PermissionValue;
    events?: PermissionValue;
    reviews?: PermissionValue;
    charity?: PermissionValue;
    projects?: PermissionValue;
    payments?: PermissionValue;
    members?: PermissionValue;
    gallery?: PermissionValue;
    show_Analytical: 'show' | 'hide';
    Show_Payment_Methods: 'show' | 'hide';
    [key: string]: PermissionValue | undefined;
  };
  branches?: Branches[];
  entities?: Entity[];
  contact_info?: {
    jobTitle?: string;
    department?: string;
    contactName?: string;
    primaryPhone?: string;
    primaryEmail?: string;
    whatsappNumber?: string;
    phone?: string;
    permissions?: {
      Show_Products?: PermissionValue;
      Show_Services?: PermissionValue;
      Show_Events?: PermissionValue;
      Show_Reviews?: PermissionValue;
      Show_Charity?: PermissionValue;
      Show_Projects?: PermissionValue;
      Show_Payments?: PermissionValue;
      Show_Members?: PermissionValue;
      Show_Gallery?: PermissionValue;
      [key: string]: PermissionValue | undefined;
    };
    email?: string;
  };
}
type ViewMode = 'grid' | 'list';
type PermissionValue = 'show' | 'hide';
@Component({
  selector: 'app-cards',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    RouterModule,
    HttpClientModule,
    MaterialModule,
    BreadcrumbComponent,
  ],
  templateUrl: './cards.component.html',
  styleUrls: ['./cards.component.scss'],
})
export class CardsComponent implements OnInit {
  cards!: any; // Rx-style getter from service
  loading!: any;
  error!: any;
  currentUser: any;
  viewMode: ViewMode = 'grid';
  currentViewMode: string = 'grid'; // For mat-button-toggle-group
  @Input() card: DigitalCard | null = null;

  // Material Table columns for list view
  cardsDisplayedColumns: string[] = [
    'avatar',
    'name',
    'company',
    'handler',
    'status',
    'actions',
  ];

  // Mobile UI state
  searchExpanded = false;
  mobileMenuOpen = false;
  environment = environment;

  // Edit form state
  showCreateForm = false;
  isEditing = false;
  editingCard: DigitalCard | null = null;

  // Delete tracking
  deletingCardIds = new Set<string>();

  // Create Card Form
  creating = false;
  createError: BusinessError | null = null;
  newQuickLinks: Array<{
    title: string;
    url: string;
    description?: string;
    openInNewTab?: boolean;
  }> = [];
  newSocialLinks: Array<{
    platform: string;
    url: string;
    label?: string;
    openInNewTab?: boolean;
  }> = [];
  newCardForm: CreateCardForm = {
    businessName: '',
    name: '',
    phone: '',
    email: '',
    handler: '',
    language: 'en',
    type: 'Business',
    industry: '',
    website: '',
    address: '',
    dob: '',
    gender: '',
    photo: '',
    companyName: '',
    jobTitle: '',
    department: '',
    location: '',
  };

  // Options from constants
  languageOptions = LANGUAGE_OPTIONS;
  cardTypeOptions = CARD_TYPE_OPTIONS;
  industryOptions = INDUSTRY_OPTIONS;

  // Search and filtering
  searchTerm = '';
  statusFilter: 'all' | 'NotVerified' | 'Verified' = 'all';

  // Chat state
  showChat = false; // default: closed
  showChatPanel = false; // selection panel visibility
  selectedCardHandler: string | null = null;

  constructor(
    private cardsService: CardsService,
    private authService: AuthService,
    private router: Router,
    private platformConfig: PlatformConfigService
  ) {}

    goToUpgradeLicense() {
    this.router.navigate(['/upgrade-license']);
  }
  
  onOverlayClick(event: MouseEvent) {
    if (event.target === event.currentTarget) {
      this.closeCreateForm();
    }
  }

  // Simple debug method - call only when needed
  debugInfo() {
    console.group('Cards Component Debug');
    console.log('Cards count:', this.cards()?.length || 0);
    console.log('Loading:', this.loading());
    console.log('Has error:', !!this.error());
    console.log('Current user:', this.currentUser?.uid || 'Not authenticated');
    console.log(
      'Chat open:',
      this.showChat,
      'Chat panel:',
      this.showChatPanel,
      'Selected handler:',
      this.selectedCardHandler
    );
    console.groupEnd();
  }

  ngOnInit() {
    this.cards = this.cardsService.cards;
    this.loading = this.cardsService.loading;
    this.error = this.cardsService.error;
    this.loadCards();
    this.loadCurrentUser();
    console.log('Current user object:', this.currentUser);

    // Ensure chat starts closed (only icon shown)
    this.showChat = false;
    this.showChatPanel = false;

    // Populate industry options from platform config (if available). We keep a default
    // placeholder option and then append categories from the platform config.
    const buildIndustryOptions = (categories: Category[] | undefined) => {
      const defaultOption = { value: '', label: 'Select Industry' };
      if (!categories || categories.length === 0) {
        // Keep existing static options as fallback
        this.industryOptions = INDUSTRY_OPTIONS;
        return;
      }

      // Map categories to {value,label} shape expected by the template
      const dynamicOptions = categories.map(cat => ({ value: cat.name, label: cat.name }));

      // Prepend default placeholder
      this.industryOptions = [defaultOption, ...dynamicOptions];
    };

    // If config already loaded synchronously, use it
    const currentCategories = this.platformConfig.getCategories();
    if (currentCategories && currentCategories.length) {
      buildIndustryOptions(currentCategories);
    }

    // Also subscribe to config$ to react to future updates (APP_INITIALIZER loads config on app start)
    this.platformConfig.config$.subscribe(cfg => {
      try {
        buildIndustryOptions(cfg?.config?.categories || []);
      } catch (e) {
        // ignore and leave fallback options
      }
    });
  }

  loadCurrentUser() {
    this.currentUser = this.authService.currentUser;

    if (!this.currentUser) {
      console.warn('No authenticated user found');
    }
  }

  async loadCards() {
    try {
      await this.cardsService.getUserCards();
    } catch (error: any) {
      console.error('Failed to load cards:', error.message || error);
    }
  }

  // --- Chat Integration ---

  /**
   * Toggle behaviour:
   * - If chat is open => close it.
   * - If chat closed => toggle the selection panel (so clicking FAB shows the small panel).
   */
  toggleChat(): void {
    if (this.showChat) {
      // If chat is currently open, close it completely.
      this.closeChat();
    } else {
      // show/hide the selection panel
      this.showChatPanel = !this.showChatPanel;
    }
  }

  closeChat() {
    this.showChat = false;
    // keep selection panel closed as well
    this.showChatPanel = false;
    // optionally clear selectedCardHandler? leave it so clicking icon again shows last selection in panel
  }

  closeChatPanel() {
    this.showChatPanel = false;
  }

  /**
   * Called when user picks a card in the panel and clicks "Open Chat"
   */
  startChat() {
    // showChat = true will render <app-chat-bot [handlerId]="selectedCardHandler">
    this.showChat = true;
    this.showChatPanel = false;
  }

  /**
   * Quick action from card "chat" button
   */
  openChatWithCard(card: DigitalCard) {
    this.selectedCardHandler = card.handler || null;
    this.showChat = true;
    this.showChatPanel = false;
    // Ensure Chat service / selected card state is available to other parts if needed
    this.cardsService.setSelectedCard(card);
  }

  async refresh() {
    await this.cardsService.getUserCards(true);
  }

  // Navigation
  navigateToCard(cardId: string) {
    this.router.navigate(['/cards', cardId]);
  }

  navigateToEdit(cardId: string) {
    this.router.navigate(['/cards', cardId, 'edit']);
  }

  navigateToPreview(cardId: string) {
    this.router.navigate(['/cards', cardId, 'preview']);
  }

  // View mode methods
  toggleView(mode: ViewMode | string) {
    this.viewMode = mode as ViewMode;
    this.currentViewMode = mode as string;
  }

  isGridView(): boolean {
    return this.viewMode === 'grid';
  }

  isListView(): boolean {
    return this.viewMode === 'list';
  }

  // Mobile UI methods
  toggleMobileSearch() {
    this.searchExpanded = true;
    this.mobileMenuOpen = false;
    // Focus the search input after the view updates
    setTimeout(() => {
      const searchInput = document.querySelector(
        '.mobile-search-input'
      ) as HTMLInputElement;
      if (searchInput) {
        searchInput.focus();
      }
    }, 100);
  }

  closeMobileSearch() {
    this.searchExpanded = false;
  }

  toggleMobileMenu() {
    this.mobileMenuOpen = !this.mobileMenuOpen;
    this.searchExpanded = false;
  }
  /** Total used licenses = each card counts as 1 + card.employeescount (if present) */
  public getUsedLicenses(): number {
    const items = this.cards ? (this.cards() || []) : [];
    return items.reduce((sum: number, c: any) => {
      const employees = Number(c?.employeescount) || 0;
      return sum + 1 + employees;
    }, 0);
  }

  get isLicenseLimitReached(): boolean {
    const licenseValue = parseInt(this.currentUser?.license || '0', 10);
    if (!licenseValue) return false;
    const used = this.getUsedLicenses();
    return used >= licenseValue;
  }
  // Filtering methods
  get filteredCards(): DigitalCard[] {
    const rawTerm = this.searchTerm.trim();
    if (!rawTerm) {
      return this.cards() || [];
    }

    const term = rawTerm.toLowerCase();
    const cleanPhoneTerm = term.replace(/^\+91/, '').replace(/\D/g, '');
    console.log('🔍 Filtering by term:', `"${term}"`);
    console.log('📞 Phone digits:', cleanPhoneTerm);

    return (this.cards() || []).filter((card: DigitalCard) => {
      const contactName = (card.contact_info?.contactName || '').toLowerCase();
      const businessName = (
        card.business_info?.businessName || ''
      ).toLowerCase();
      const handler = (card.handler || '').toLowerCase();
      const primaryPhone = (card.contact_info?.primaryPhone || '')
        .replace(/^\+91/, '')
        .replace(/\D/g, '');
      const matches =
        contactName.startsWith(term) ||
        businessName.startsWith(term) ||
        handler.startsWith(term) ||
        (cleanPhoneTerm && primaryPhone.startsWith(cleanPhoneTerm));

      if (matches) {
        console.log('✅ Matched card:', {
          id: card.id,
          contactName: card.contact_info?.contactName,
          businessName: card.business_info?.businessName,
          handler: card.handler,
          phone: card.contact_info?.primaryPhone,
        });
      }

      return matches;
    });
  }
  onSearchChange(): void {
    this.searchTerm = this.searchTerm.trimStart();
    console.log('searchTerm:', `"${this.searchTerm}"`);
  }

  showLicensePopup = false; 
  // Create card methods
  openCreateForm(card?: DigitalCard) {
  const total = parseInt(this.currentUser?.license || '0', 10);
  const used = this.getUsedLicenses();

  // ✅ Block creation if limit is reached
  if (total > 0 && used >= total) {
    this.createError = {
      type: 'license',
      title: 'License Limit Reached',
      message: 'You have reached your license limit. Please upgrade your plan to create more cards.',
    };

    this.showCreateForm = false;   // ✅ BLOCK FORM
    this.showLicensePopup = true; // ✅ SHOW LICENSE POPUP
    return;                        // ✅ STOP EXECUTION
  }

  // ✅ Allowed → continue with creation logic
  this.showCreateForm = true;
  this.isEditing = !!card;

  this.editingCard = card
    ? (JSON.parse(JSON.stringify(card)) as DigitalCard)
    : null;

  if (this.isEditing && card) {
    this.newCardForm = {
      businessName: card.business_info?.businessName || '',
      name: card.contact_info?.contactName || '',
      phone: card.contact_info?.primaryPhone || '',
      email: card.contact_info?.primaryEmail || '',
      handler: card.handler || '',
      language: card.language || 'en',
      type: card.type || 'Business',
      industry: card.industry || '',
    };
  } else {
    this.resetForm();
  }

  this.mobileMenuOpen = false;
}

closeLicensePopup() {
  this.showLicensePopup = false;
}


  closeCreateForm() {
    this.showCreateForm = false;
    this.resetForm();
  }

  resetForm() {
    this.newCardForm = {
      name: '',
      phone: '',
      email: '',
      handler: '',
      website: '', // Add these missing fields
      language: 'en',
      type: 'Business',
      industry: '',
    };
    this.createError = null;
    // reset new link buffers
    this.newQuickLinks = [];
    this.newSocialLinks = [];
  }
  remainingCards(): string {
    const licenseValue = parseInt(this.currentUser?.license || '0', 10);
    const used = this.getUsedLicenses();
    // show "used of total" so it's clear how many licenses are consumed
    return `Active Cards : ${used} of ${licenseValue || 0}`;
  }

  /** Number of licenses left (non-negative) */
  get remainingLicenses(): number {
    const licenseValue = parseInt(this.currentUser?.license || '0', 10);
    if (!licenseValue) return 0;
    const used = this.getUsedLicenses();
    return Math.max(0, licenseValue - used);
  }
  async createCard() {
    if (!this.newCardForm) {
      this.resetForm();
    }
    try {
      this.creating = true;
      this.createError = null;

      // ✅ Validate required fields
      if (!this.newCardForm.name?.trim()) {
        this.createError = {
          type: 'validation',
          title: 'Validation Error',
          message: 'Contact name is required',
          code: 'MISSING_NAME',
        };
        return;
      }

      if (!this.newCardForm.phone?.trim()) {
        this.createError = {
          type: 'validation',
          title: 'Validation Error',
          message: 'Phone number is required',
          code: 'MISSING_PHONE',
        };
        return;
      }

      if (!this.newCardForm.handler?.trim()) {
        this.createError = {
          type: 'validation',
          title: 'Validation Error',
          message: 'Handler is required',
          code: 'MISSING_HANDLER',
        };
        return;
      }

      // ✅ Prepare card request object
      const cardRequest: CardCreateRequest = {
        contactName: this.newCardForm.name.trim(),
        contactPhone: this.newCardForm.phone.trim(),
        handler: this.newCardForm.handler.trim(),
        contactEmail: this.newCardForm.email?.trim() || '',
        language: this.newCardForm.language,
        type: this.newCardForm.type,
        uid: this.authService.currentUser?.uid || '',
        industry: this.newCardForm.industry,
        businessName: this.newCardForm.businessName?.trim() || '',
      };

      // ✅ UPDATE FLOW
      if (this.isEditing && this.editingCard?.id) {
        try {
          await this.cardsService.updateCardPersonalInfo(this.editingCard.id, {
            address: this.newCardForm.address,
            dob: this.newCardForm.dob,
            gender: this.newCardForm.gender,
            mobile: this.newCardForm.phone,
            profilePicture: this.newCardForm.photo,
          });

          await this.cardsService.updateCardContact(this.editingCard.id, {
            phone: this.newCardForm.phone,
            email: this.newCardForm.email,
          });

          await this.cardsService.updateCardBusinessInfo(this.editingCard.id, {
            businessName: this.newCardForm.businessName,
            description: this.editingCard.business_info?.description,
            companyWebsite: this.editingCard.business_info?.companyWebsite,
            companyPhone: this.editingCard.business_info?.companyPhone,
            companyEmail: this.editingCard.business_info?.companyEmail,
            companyAddress: this.editingCard.business_info?.companyAddress,
          });

          this.closeCreateForm();
        } catch (err) {
          console.error('Card update failed:', err);
          this.createError = {
            type: 'business',
            title: 'Update Error',
            code: 'CARD_UPDATE_ERROR',
            message: 'Failed to update card',
          };
        }
      }

      // ✅ CREATE FLOW
      else {
        (cardRequest as any).quickLinks = this.newQuickLinks.map((l) => ({
          title: l.title,
          url: l.url,
          description: l.description || '',
          openInNewTab: !!l.openInNewTab,
        }));

        (cardRequest as any).socialLinks = this.newSocialLinks.map((l) => ({
          platform: l.platform,
          url: l.url,
          label: l.label || '',
          openInNewTab: !!l.openInNewTab,
        }));

        const response = await this.cardsService.createCard(cardRequest);

        if (response.success && response.data?.id) {
          const createdId = response.data.id;

          try {
            if (this.newQuickLinks?.length > 0) {
              const quickPayload = this.newQuickLinks.map((l) => ({
                title: l.title,
                url: l.url,
              }));
              await this.cardsService.updateCardQuickLinks(
                createdId,
                quickPayload as any
              );
            }
          } catch (err) {
            console.error('Failed to add quick links:', err);
          }

          try {
            if (this.newSocialLinks?.length > 0) {
              const socialPayload = this.newSocialLinks.map((l) => ({
                platform: l.platform,
                url: l.url,
              }));
              await this.cardsService.updateCardSocialLinks(
                createdId,
                socialPayload as any
              );
            }
          } catch (err) {
            console.error('Failed to add social links:', err);
          }

          this.closeCreateForm();
          this.router.navigate(['/cards', createdId, 'edit']);
        } else if (!response.success) {
          this.createError = response.error;
        }
      }
    } catch (error: any) {
      console.error('Error creating/updating card:', error);
      this.createError = {
        type: 'business',
        title: 'Card Error',
        code: 'CARD_ERROR',
        message: error.message || 'Failed to process card',
      };
    } finally {
      this.creating = false;
    }
  }

  trackByCardId(index: number, card: DigitalCard): string {
    return card.id;
  }

  // Quick actions
  addQuickLink(card: DigitalCard | null) {
    // If card is null, we're in create mode -> use newQuickLinks buffer
    if (!card) {
      this.newQuickLinks.push({
        title: '',
        url: '',
        description: '',
        openInNewTab: false,
      });
      return;
    }

    if (!card.quick_links) {
      card.quick_links = [];
    }

    card.quick_links.push({
      title: '', // Changed from 'name' to 'title'
      url: '',
      description: '',
      openInNewTab: false,
    });
  }

  addSocialLink(card: DigitalCard | null) {
    // If card is null, create-mode
    if (!card) {
      this.newSocialLinks.push({
        platform: '',
        url: '',
        label: '',
        openInNewTab: false,
      });
      return;
    }

    if (!card.social_links) {
      card.social_links = [];
    }

    card.social_links.push({
      platform: '', // User will select platform (LinkedIn, Twitter, etc.)
      url: '',
      label: '',
      openInNewTab: false,
    });
  }

  removeSocialLink(card: DigitalCard | null, index: number) {
    if (!card) {
      if (index >= 0 && index < this.newSocialLinks.length)
        this.newSocialLinks.splice(index, 1);
      return;
    }
    if (!card.social_links) return;
    card.social_links.splice(index, 1);
  }

  removeQuickLink(card: DigitalCard | null, index: number) {
    if (!card) {
      if (index >= 0 && index < this.newQuickLinks.length)
        this.newQuickLinks.splice(index, 1);
      return;
    }
    if (!card.quick_links) return;
    card.quick_links.splice(index, 1);
  }

  // Get status badge class
  getStatusBadgeClass(isActive: boolean): string {
    return isActive ? 'badge-success' : 'badge-secondary';
  }

  editCard(card: DigitalCard) {
    this.router.navigate(['/card', card.id, 'edit']);
  }

  // Format date
  formatDate(dateString: string): string {
    try {
      return new Date(dateString).toLocaleDateString();
    } catch {
      return 'N/A';
    }
  }

  // Mobile dropdown actions
  importCards() {
    // TODO: Implement import functionality
    this.mobileMenuOpen = false;
  }

  exportCards() {
    // TODO: Implement export functionality
    this.mobileMenuOpen = false;
  }

  copyAllCards() {
    // TODO: Implement copy all functionality
    this.mobileMenuOpen = false;
  }

  deleteAllCards() {
    if (
      confirm(
        'Are you sure you want to delete all cards? This action cannot be undone.'
      )
    ) {
      // TODO: Implement delete all functionality
    }
    this.mobileMenuOpen = false;
  }

  flipCard(cardElement: HTMLElement) {
    cardElement.classList.toggle('flipped');
  }

  showOverlay(cardElement: HTMLElement) {
    cardElement.classList.add('hovered');
  }

  hideOverlay(cardElement: HTMLElement) {
    cardElement.classList.remove('hovered');
  }

  /** Utility: Convert hex to RGB */
  hexToRgb(hex: string) {
    hex = hex.replace('#', '');
    const bigint = parseInt(hex, 16);
    const r = (bigint >> 16) & 255;
    const g = (bigint >> 8) & 255;
    const b = bigint & 255;
    return { r, g, b };
  }

  /** Utility: Compute average color between two hex values */
  averageHex(hex1: string, hex2: string) {
    const c1 = this.hexToRgb(hex1);
    const c2 = this.hexToRgb(hex2);
    const r = Math.round((c1.r + c2.r) / 2);
    const g = Math.round((c1.g + c2.g) / 2);
    const b = Math.round((c1.b + c2.b) / 2);
    return '#' + ((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1);
  }

  /** Utility: Check if a color is dark */
  isDark(hex: string) {
    const { r, g, b } = this.hexToRgb(hex);
    const luminance = 0.299 * r + 0.587 * g + 0.114 * b;
    return luminance < 140;
  }
  navigateToHandlerLink(card: DigitalCard): void {
    if (!card?.handler) {
      console.warn('No handler available for navigation');
      return;
    }

    const link = `${environment.appUrl}/${card.handler}`;

    window.open(link, '_blank', 'noopener,noreferrer');
  }
  /** Get smart text color for given gradient colors */
  getSmartTextColor(color1?: string, color2?: string): string {
    const c1 = color1 || '#ffffff';
    const c2 = color2 || c1;

    const hexToRgb = (hex: string) => {
      const cleaned = hex.replace('#', '');
      const bigint = parseInt(cleaned, 16);
      return {
        r: (bigint >> 16) & 255,
        g: (bigint >> 8) & 255,
        b: bigint & 255,
      };
    };

    const rgb1 = hexToRgb(c1);
    const rgb2 = hexToRgb(c2);

    // Perceived brightness formula
    const brightness1 = Math.sqrt(
      0.299 * rgb1.r * rgb1.r +
        0.587 * rgb1.g * rgb1.g +
        0.114 * rgb1.b * rgb1.b
    );

    const brightness2 = Math.sqrt(
      0.299 * rgb2.r * rgb2.r +
        0.587 * rgb2.g * rgb2.g +
        0.114 * rgb2.b * rgb2.b
    );

    // Pick the darker brightness as reference for contrast
    const minBrightness = Math.min(brightness1, brightness2);

    // Return black or white based on which contrasts more
    return minBrightness > 130 ? '#000000' : '#ffffff';
  }

  /**
   * viewCard used to navigate to view page.
   * Important: we NO LONGER auto-open chat during navigation.
   */
  viewCard(card: DigitalCard) {
    // set selected card context (for other services/components if needed)
    this.cardsService.setSelectedCard(card);
    // set selected handler but do not open chat automatically
    this.selectedCardHandler = card.handler || null;
    // navigate
    setTimeout(() => {
      this.router.navigate(['/view', card.id]);
    }, 50);
  }

  copyCardUrl(card: DigitalCard) {
    const url = `${window.location.origin}/card/${card.handler}`;
    navigator.clipboard
      .writeText(url)
      .then(() => {
        // TODO: Show success toast/snackbar
      })
      .catch((err) => {
        console.error('Failed to copy URL:', err);
      });
  }
}
